import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';
import { Dayjs } from 'dayjs';

import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';

import {
  AddressInfo,
  DetailInfo,
  EditActions,
  GeneralInfo,
  UserInfo,
  FeesInfo,
} from './components';

import {
  UpdateProfileInfoDTO,
  UpdateProfileProviderDTO,
  useDisableMerchantMutation,
  useEnableMerchantMutation,
  useGetProfileQuery,
  useGetRateMarkupGroupsQuery,
  useUpdateAddressMutation,
  useUpdateProfileInfoMutation,
  useUpdateProfileStatusMutation,
  useUpdateProfiveProviderMutation,
} from '../../../api';
import { dateToUTC } from '../../../utils/dates';
import { ProfileStatus, ProfileType } from '../../../enums';
import type { Address, ProfileWithInfo } from '../../../types';
import type { InfoFieldChangeHandler } from '../../../components';
import { MerchantInfo } from './components/MerchantInfo';
import { permissionsCheck } from '../../../utils/permission';

export const Details: FC = () => {
  const { t } = useTranslation();
  const { profileUid } = useParams<'profileUid'>();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToViewAddress = permissionsCheck(['READ_ADDRESS']);
  const hasPermissionToEditAddress = permissionsCheck(['WRITE_ADDRESS']);
  const hasPermissionToEditProfiles = permissionsCheck(['WRITE_PROFILES']);

  // api
  const { data: profile, isLoading: isProfileLoading } = useGetProfileQuery(
    profileUid ? { profileUid } : skipToken
  );
  const [updateProfileStatus, { isLoading: isUpdateProfileStatusLoading }] =
    useUpdateProfileStatusMutation();
  const [updateProfileProvider, { isLoading: isUpdateProfileProviderLoading }] =
    useUpdateProfiveProviderMutation();
  const [updateProfileInfo, { isLoading: isUpdateProfileInfoLoading }] =
    useUpdateProfileInfoMutation();
  const [updateAddressInfo, { isLoading: isUpdateAddressInfoLoading }] = useUpdateAddressMutation();
  const [enableMerchant, { isLoading: isEnableMerchantLoading }] = useEnableMerchantMutation();
  const [disableMerchant, { isLoading: isDisableMerchantLoading }] = useDisableMerchantMutation();
  const { data: rateMarkupGroups } = useGetRateMarkupGroupsQuery({});

  // variables
  const commonLoading =
    isProfileLoading ||
    isUpdateProfileStatusLoading ||
    isUpdateProfileInfoLoading ||
    isUpdateAddressInfoLoading ||
    isEnableMerchantLoading ||
    isUpdateProfileProviderLoading ||
    isDisableMerchantLoading;
  const profileType = profile?.profile.type;

  // fields control logic (general)
  const [editGeneralFields, setEditGeneralFields] = useState<Partial<ProfileWithInfo> | null>(null);
  const handleEditGeneralFields: InfoFieldChangeHandler = (e) => {
    // @ts-ignore
    const name = e.target.name;
    // @ts-ignore
    const value = e.target.value;
    // @ts-ignore
    const checked = e.target.checked;
    let mappedValue = value;
    if (typeof value === 'string' && value.length === 0) {
      mappedValue = null;
    }

    if (checked !== undefined) {
      mappedValue = checked;
    }

    setEditGeneralFields((prevState) => {
      if (!prevState) {
        return {
          [name]: mappedValue,
        };
      }

      return {
        ...prevState,
        [name]: mappedValue,
      };
    });
  };
  const [editProviderFields, setEditProviderFields] = useState<
    UpdateProfileProviderDTO['body'] | null
  >(null);
  const handleEditProfiderFields: InfoFieldChangeHandler = (e) => {
    // @ts-ignore
    const name = e.target.name;
    // @ts-ignore
    const value = e.target.value;
    // @ts-ignore
    const checked = e.target.checked;

    let mappedValue = value;
    if (typeof value === 'string' && value.length === 0) {
      mappedValue = null;
    }

    if (checked !== undefined) {
      mappedValue = checked;
    }
    // @ts-ignore
    setEditProviderFields((prevState) => {
      if (!prevState) {
        return {
          [name]: mappedValue,
        };
      }

      return {
        ...prevState,
        [name]: mappedValue,
      };
    });
  };
  const handleEditGeneralMerchant: InfoFieldChangeHandler = async () => {
    if (!profile) return;
    try {
      if (!profile?.profile.enabled_as_merchant_at) {
        enableMerchant({ profileUid: profile.profile.uid });
      } else {
        disableMerchant({ profileUid: profile.profile.uid });
      }
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  // validation for required fields (general)
  const requiredGeneralFields = ['status_reason'] as (keyof Partial<ProfileWithInfo>)[];
  const checkGeneralSubmitDisabling = () => {
    if (!editGeneralFields) return true;

    for (const field of requiredGeneralFields) {
      if (
        field === 'status_reason' &&
        (editGeneralFields.status === ProfileStatus.NEW ||
          editGeneralFields.status === ProfileStatus.VERIFIED)
      ) {
        return false;
      } else if (
        editGeneralFields[field] === undefined ||
        editGeneralFields[field] === '' ||
        editGeneralFields[field] === null
      ) {
        return true;
      }
    }

    return false;
  };
  const checkProviderSubmitDisabling = () => {
    if (!editProviderFields || !editProviderFields.provider) return true;

    return false;
  };
  // fields control logic (detail)
  const [editDetailFields, setEditDetailFields] = useState<Partial<ProfileWithInfo> | null>(null);
  const handleEditDetailFields: InfoFieldChangeHandler = (e, notEvent, fieldName) => {
    // @ts-ignore
    const name = notEvent && fieldName ? fieldName : e.target.name;
    // @ts-ignore
    const value = notEvent ? e : e.target.value;
    let mappedValue = value;
    if (typeof value === 'string' && value.length === 0) {
      mappedValue = null;
    }

    setEditDetailFields((prevState) => {
      if (!prevState) {
        return {
          info: {
            [name]: mappedValue,
          },
        };
      }

      return {
        ...prevState,
        info: {
          ...prevState.info,
          [name]: mappedValue,
        },
      };
    });
  };

  // fields control logic (address)
  const [editAddressFields, setEditAddressFields] = useState<Partial<Address> | null>(null);
  const handleEditAddressFields: InfoFieldChangeHandler = (e) => {
    // @ts-ignore
    const name = e.target.name;
    // @ts-ignore
    const value = e.target.value;

    let mappedValue = value;
    if (typeof value === 'string' && value.length === 0) {
      mappedValue = null;
    }

    setEditAddressFields((prevState) => {
      if (!prevState) {
        return {
          [name]: mappedValue,
        };
      }

      return {
        ...prevState,
        [name]: mappedValue,
      };
    });
  };

  // edit mode logic (general)
  const [isEditGeneral, setEditGeneralStatus] = useState(false);
  const openGeneralEdit = () => {
    profile?.profile &&
      setEditGeneralFields({
        enabled_as_merchant: !!profile.profile.enabled_as_merchant_at,
        ...profile.profile,
      });
    setEditGeneralStatus(true);
  };
  const closeGeneralEdit = () => {
    setEditGeneralStatus(false);
    setEditGeneralFields(null);
  };
  const [isEditProvider, setEditProvider] = useState(false);
  const openProviderEdit = () => {
    profile?.profile &&
      setEditProviderFields({
        cascade: true,
        provider: profile.profile.default_provider,
      });
    setEditProvider(true);
  };
  const closeProviderEdit = () => {
    setEditProvider(false);
    setEditProviderFields(null);
  };

  // edit mode logic (fees)
  const [isEditFees, setEditFeesStatus] = useState(false);
  const openFeesEdit = () => {
    profile?.profile && setEditGeneralFields(profile.profile);
    setEditFeesStatus(true);
  };
  const closeFeesEdit = () => {
    setEditFeesStatus(false);
    setEditGeneralFields(null);
  };

  // edit mode logic (merchant)
  const [isEditMerchant, setEditMerchantStatus] = useState(false);
  const openMerchantEdit = () => {
    profile?.profile && setEditGeneralFields(profile.profile);
    setEditMerchantStatus(true);
  };
  const closeMerchantEdit = () => {
    setEditMerchantStatus(false);
    setEditGeneralFields(null);
  };

  // edit mode logic (detail)
  const [isEditDetail, setEditDetailStatus] = useState(false);
  const openDetailEdit = () => {
    profile?.profile && setEditDetailFields(profile.profile);
    setEditDetailStatus(true);
  };
  const closeDetailEdit = () => {
    setEditDetailStatus(false);
    setEditDetailFields(null);
  };

  // edit mode logic (address)
  const [isEditAddress, setEditAddressStatus] = useState(false);
  const openAddressEdit = () => {
    profile?.profile.addresses[0] && setEditAddressFields(profile?.profile.addresses[0]);
    setEditAddressStatus(true);
  };
  const closeAddressEdit = () => {
    setEditAddressStatus(false);
    setEditAddressFields(null);
  };

  // edit handler (general)
  const editGeneralInfo = async () => {
    if (!profile || !editGeneralFields) return;

    const args = {
      profileUid: profile.profile.uid,
      body: {
        status: editGeneralFields.status!,
        status_reason:
          editGeneralFields.status === ProfileStatus.BLOCKED ||
          editGeneralFields.status === ProfileStatus.ARCHIVED ||
          editGeneralFields.status === ProfileStatus.REJECTED
            ? (editGeneralFields.status_reason as string)
            : undefined,
      },
    };

    try {
      await updateProfileStatus(args).unwrap();
      closeGeneralEdit();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const editProviderInfo = async () => {
    if (!profile || !editProviderFields) return;

    const args = {
      profile_uid: profile.profile.uid,
      body: {
        provider: editProviderFields.provider,
        cascade: editProviderFields.cascade,
      },
    };

    try {
      await updateProfileProvider(args).unwrap();
      closeProviderEdit();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const editFeeInfo = async () => {
    if (!profile || !editGeneralFields) return;

    const args = {
      profileUid: profile.profile.uid,
      body: {
        fee_group_id: editGeneralFields.fee_group_id,
        rate_markup_group_id: editGeneralFields.rate_markup_group_id,
        merchant_rate_markup_group_id:
          editGeneralFields.merchant_rate_markup_group_id === -1
            ? null
            : editGeneralFields.merchant_rate_markup_group_id,
      },
    };

    try {
      await updateProfileStatus(args).unwrap();
      closeFeesEdit();
      closeMerchantEdit();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // edit handler (detail)
  const editDetailInfo = async () => {
    if (!profile || !editDetailFields) return;

    const info =
      profileType === ProfileType.LEGAL
        ? {
            company_name: editDetailFields.info?.company_name,
            registration_number: editDetailFields.info?.registration_number,
            registration_country_code: editDetailFields.info?.registration_country_code,
            registration_date: editDetailFields.info?.registration_date
              ? dateToUTC(editDetailFields.info.registration_date as unknown as Dayjs)
              : null,
            additional_business_names: editDetailFields.info?.additional_business_names,
            formation_date: editDetailFields.info?.formation_date
              ? dateToUTC(editDetailFields.info.formation_date as unknown as Dayjs)
              : null,
            license_valid_to: editDetailFields.info?.license_valid_to
              ? dateToUTC(editDetailFields.info.license_valid_to as unknown as Dayjs)
              : null,
            phone: editDetailFields.info?.phone,
            email: editDetailFields.info?.email,
            website: editDetailFields.info?.website,
            company_type_id: editDetailFields.info?.company_type_id,
            purpose: editDetailFields.info?.purpose,
            description: editDetailFields.info?.description,
          }
        : {
            citizenship_country_code: editDetailFields.info?.citizenship_country_code,
            first_name: editDetailFields.info?.first_name,
            last_name: editDetailFields.info?.last_name,
            gender: editDetailFields.info?.gender,
            date_of_birth: editDetailFields.info?.date_of_birth
              ? dateToUTC(editDetailFields.info.date_of_birth as unknown as Dayjs)
              : null,
            birth_country_code: editDetailFields.info?.birth_country_code,
            place_of_birth: editDetailFields.info?.place_of_birth,
            tax_residence_country_code: editDetailFields.info?.tax_residence_country_code,
            tax_id_number: editDetailFields.info?.tax_id_number,
            id_document_type: editDetailFields.info?.id_document_type,
            document_number: editDetailFields.info?.document_number,
            personal_code: editDetailFields.info?.personal_code,
            issuing_authority_name: editDetailFields.info?.issuing_authority_name,
            issued_date: editDetailFields.info?.issued_date
              ? dateToUTC(editDetailFields.info.issued_date as unknown as Dayjs)
              : null,
            valid_to: editDetailFields.info?.valid_to
              ? dateToUTC(editDetailFields.info.valid_to as unknown as Dayjs)
              : null,
          };

    const args = {
      profileType: profile.profile.type.toLowerCase() as UpdateProfileInfoDTO['profileType'],
      profileUid: profile.profile.uid,
      body: { ...info },
    };

    try {
      await updateProfileInfo(args).unwrap();
      closeDetailEdit();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // edit handler (address)
  const editAddressInfo = async () => {
    if (!profile || !editAddressFields) return;

    const args = {
      addressId: profile.profile.addresses[0].id,
      profileUid: profile.profile.uid,
      body: {
        state: editAddressFields.state,
        city: editAddressFields.city,
        zip_code: editAddressFields.zip_code,
        street: editAddressFields.street,
        building_number: editAddressFields.building_number,
        flat_number: editAddressFields.flat_number,
        country_code: editAddressFields.country_code!,
      },
    };

    try {
      await updateAddressInfo(args).unwrap();
      closeAddressEdit();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  return (
    <Box>
      {/*User Info*/}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('profile-details.panels.user-info')}
        </AccordionSummary>
        <AccordionDetails>
          <UserInfo profile={profile?.profile} />
        </AccordionDetails>
      </Accordion>

      {/*General Info*/}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('profile-details.panels.general-info')}
        </AccordionSummary>
        <AccordionDetails>
          <GeneralInfo
            profile={profile?.profile}
            isEditMode={isEditGeneral}
            isEditProviderMode={isEditProvider}
            fields={editGeneralFields}
            providerFields={editProviderFields}
            fieldsHandler={handleEditGeneralFields}
            fieldsHandlerProvider={handleEditProfiderFields}
            isLoading={commonLoading}
          />
        </AccordionDetails>

        {!profile?.profile.read_only && hasPermissionToEditProfiles && (
          <AccordionActions>
            <EditActions
              isEditMode={isEditGeneral}
              startEdit={() => openGeneralEdit()}
              endEdit={() => closeGeneralEdit()}
              saveHandler={() => editGeneralInfo()}
              isLoading={commonLoading}
              submitDisabling={checkGeneralSubmitDisabling()}
              secondSubmitDisabling={checkProviderSubmitDisabling()}
              endSecondEdit={closeProviderEdit}
              startSecondEdit={openProviderEdit}
              saveSecondHandler={editProviderInfo}
              isSecondEditMode={isEditProvider}
              secondEditButtonTitle={t('profile-details.action-btn.edit-provider')}
            />
          </AccordionActions>
        )}
      </Accordion>

      {/*Merchant*/}
      {/* {profile?.profile.type === 'LEGAL' && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {t('profile-details.panels.merchant')}
          </AccordionSummary>
          <AccordionDetails>
            <MerchantInfo
              profile={profile?.profile}
              isEditMode={isEditMerchant}
              rateMarkupGroups={rateMarkupGroups}
              fields={editGeneralFields}
              fieldsHandler={handleEditGeneralFields}
              enableMerchantHandler={handleEditGeneralMerchant}
              isLoading={commonLoading}
            />
          </AccordionDetails>

          {hasPermissionToEditProfiles && (
            <AccordionActions>
              <EditActions
                isEditMode={isEditMerchant}
                startEdit={() => openMerchantEdit()}
                endEdit={() => closeMerchantEdit()}
                saveHandler={() => editFeeInfo()}
                isLoading={commonLoading}
                submitDisabling={checkGeneralSubmitDisabling()}
              />
            </AccordionActions>
          )}
        </Accordion>
      )} */}

      {/*Fees*/}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('profile-details.panels.fees')}
        </AccordionSummary>
        <AccordionDetails>
          <FeesInfo
            profile={profile?.profile}
            isEditMode={isEditFees}
            rateMarkupGroups={rateMarkupGroups}
            fields={editGeneralFields}
            fieldsHandler={handleEditGeneralFields}
            isLoading={commonLoading}
          />
        </AccordionDetails>

        {hasPermissionToEditProfiles && (
          <AccordionActions>
            <EditActions
              isEditMode={isEditFees}
              startEdit={() => openFeesEdit()}
              endEdit={() => closeFeesEdit()}
              saveHandler={() => editFeeInfo()}
              isLoading={commonLoading}
              submitDisabling={checkGeneralSubmitDisabling()}
            />
          </AccordionActions>
        )}
      </Accordion>

      {/*Detail Info*/}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('profile-details.panels.detail-info')}
        </AccordionSummary>
        <AccordionDetails>
          <DetailInfo
            profile={profile?.profile}
            isEditMode={isEditDetail}
            fields={editDetailFields}
            fieldsHandler={handleEditDetailFields}
            isLoading={commonLoading}
          />
        </AccordionDetails>

        {!profile?.profile.read_only && hasPermissionToEditProfiles && (
          <AccordionActions>
            <EditActions
              isEditMode={isEditDetail}
              startEdit={() => openDetailEdit()}
              endEdit={() => closeDetailEdit()}
              saveHandler={() => editDetailInfo()}
              isLoading={commonLoading}
            />
          </AccordionActions>
        )}
      </Accordion>

      {/*Address*/}
      {hasPermissionToViewAddress && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {t('profile-details.panels.address')}
          </AccordionSummary>
          <AccordionDetails>
            <AddressInfo
              address={profile?.profile.addresses[0]}
              isEditMode={isEditAddress}
              fields={editAddressFields}
              fieldsHandler={handleEditAddressFields}
              isLoading={commonLoading}
            />
          </AccordionDetails>

          {!profile?.profile.read_only && hasPermissionToEditAddress && (
            <AccordionActions>
              <EditActions
                isEditMode={isEditAddress}
                startEdit={() => openAddressEdit()}
                endEdit={() => closeAddressEdit()}
                saveHandler={() => editAddressInfo()}
                isLoading={commonLoading}
              />
            </AccordionActions>
          )}
        </Accordion>
      )}
    </Box>
  );
};
